import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, OnInit } from '@angular/core';
import { AccountSetupHelperService } from '../../../account-setup-helper.service';

@Component({
  selector: 'app-logo-selector',
  templateUrl: './logo-selector.component.html',
  styleUrls: ['./logo-selector.component.css'],
})
export class LogoSelectorComponent implements OnInit, OnChanges {
  @Input() hubName = '';
  @Input() selectedPalette: any;
  @Input() logoUrl: string | null = null;
  @Output() logoChange = new EventEmitter<string | File | null>();

  @Input() mode: 'none' | 'placeholder' | 'upload' = 'placeholder';
  @Output() modeChange = new EventEmitter<'none' | 'placeholder' | 'upload'>();
  previewUrl: string | null = null;

  constructor(public accountSetupHelperService: AccountSetupHelperService) {}

  ngOnInit(): void {
    // Restore preview from service state (set by restoreProgress or hub-branding-step init)
    this.previewUrl = this.accountSetupHelperService.wizardData.hubInput.hubBranding.placeHolderLogo;
    // Ensure we have a generated placeholder if in placeholder mode
    if (this.mode === 'placeholder' && !this.previewUrl) {
      this.regenerateLogo();
    }
  }

  /** React to input changes */
  ngOnChanges(changes: SimpleChanges): void {
    // If we're in placeholder mode and hubName, selectedPalette, or mode changed — regenerate
    if (
      this.mode === 'placeholder' &&
      (changes['hubName'] || changes['selectedPalette'] || changes['mode'])
    ) {
      this.regenerateLogo();
    }
  }

  regenerateLogo() {
    const logo = this.accountSetupHelperService.generatePlaceholderLogo(
      this.hubName || undefined,
      this.selectedPalette || undefined,
    );
    this.setGeneratedPlaceholder(logo ?? '');
  }

  setGeneratedPlaceholder(logo: string) {
    if (!logo) {
      this.accountSetupHelperService.wizardData.hubInput.hubBranding.placeHolderLogo = null;
      return;
    }
    this.previewUrl = logo;
    this.accountSetupHelperService.wizardData.hubInput.hubBranding.placeHolderLogo = logo;
    this.logoChange.emit(this.previewUrl);
  }

  /** Mode selection */
  switchMode(mode: 'placeholder' | 'upload') {
    this.mode = mode;
    this.accountSetupHelperService.wizardData.hubInput.hubBranding.logoMode = mode;
    this.modeChange.emit(mode);

    if (mode === 'placeholder') {
      const logo = this.accountSetupHelperService.generatePlaceholderLogo(
        this.hubName || undefined,
        this.selectedPalette || undefined,
      );
      this.setGeneratedPlaceholder(logo ?? '');
    } else {
      this.setGeneratedPlaceholder('');
    }
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  /** Called when the media-library emits selectedFilesChange (upload mode) */
  onCustomLogoChange(files: any[]) {
    this.accountSetupHelperService.wizardData.hubInput.hubBranding.logo = files;
    if (files?.length) {
      // Switched to custom upload — update mode if not already there
      if (this.mode !== 'upload') {
        this.mode = 'upload';
        this.accountSetupHelperService.wizardData.hubInput.hubBranding.logoMode = 'upload';
        this.modeChange.emit('upload');
      }
    } else {
      // File removed — revert to placeholder
      this.switchMode('placeholder');
    }
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  /** Called when the media-library emits selectedFilesIdsChange (upload mode) */
  onCustomLogoIdsChange(ids: number[]) {
    this.accountSetupHelperService.wizardData.hubInput.hubBranding.logo_id = ids;
    this.accountSetupHelperService.scheduleSaveProgress();
  }

  removeLogo() {
    this.previewUrl = null;
    this.mode = 'none';
    this.logoChange.emit(null);
  }
}
